<template>
  <div
    class="reply-box"
    :class="{
      'border-yellow-300 bg-yellow-50': isNote && !isMessageLengthReachingThreshold,
      'border-[#EF4444] bg-yellow-50': isNote && isMessageLengthReachingThreshold,
      'border-gray-200 bg-white': !isNote && !isMessageLengthReachingThreshold,
      'border-[#EF4444] bg-white': !isNote && isMessageLengthReachingThreshold,
    }"
  >
    <div class="reply-box__top">
      <reply-email-head
        :mode="replyType"
        :set-reply-mode="setReplyMode"
        :is-showing-reply-box.sync="isShowingReplyBox"
        :cc-emails.sync="ccEmails"
        :bcc-emails.sync="bccEmails"
        :fwd-emails.sync="fwdEmails"
        :from-email="emailFromAddress"
        :to-emails.sync="toEmails"
        @dismiss="clearMessage"
      />
      <div class="relative">
        <email-woot-message-editor
          ref="messageEditor"
          v-model="message"
          :editor-id="editorStateId"
          class="input message-editor"
          :class="isNote ? 'note-mode' : 'reply-mode'"
          :is-private="isOnPrivateNote"
          :placeholder="messagePlaceHolder"
          :update-selection-with="updateEditorSelectionWith"
          :min-height="4"
          :enable-variables="true"
          :variables="messageVariables"
          :signature="signatureToApply"
          :allow-signature="true"
          :show-toolbar="showToolbar"
          :inbox="inbox"
          :show-emoji-picker="showEmojiPicker"
          :toggle-emoji-picker="toggleEmojiPicker"
          :hide-emoji-picker="hideEmojiPicker"
          :attached-files="attachedFiles"
          :remove-attachment="removeAttachment"
          :enabled-menu-options="emailEditorMenuOptions"
          @typing-off="onTypingOff"
          @typing-on="onTypingOn"
          @focus="onFocus"
          @blur="onBlur"
          @toggle-user-mention="toggleUserMention"
          @toggle-canned-menu="toggleCannedMenu"
          @toggle-variables-menu="toggleVariablesMenu"
          @clear-selection="clearEditorSelection"
        >
          <template v-if="isMessageLengthReachingThreshold" #warning-message>
            <div class="text-xs px-4 py-1 text-right">
              <span :class="charLengthClass">
                {{ `${message.length}/${maxLength}` }}
              </span>
            </div>
          </template>
          <template #toolbar-left>
            <div
              v-if="showFileUpload && isEmailOrWebWidgetInbox"
              class="flex h-8 hover:bg-gray-100 rounded-md cursor-pointer"
            >
              <file-upload
                ref="uploadAttachment"
                v-tooltip.top="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                input-id="conversationAttachment"
                :size="4096 * 4096"
                :accept="allowedFileTypes"
                :multiple="true"
                :drop="newConversationModalActive"
                :drop-directory="false"
                :data="{
                  direct_upload_url: '/rails/active_storage/direct_uploads',
                  direct_upload: true,
                }"
                @input-file="file => fileUploadHandler(file, fileUploadOptions)"
                @input-filter="inputFilter"
              >
                <span
                  :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                  class="material-symbols-outlined message-toolbar-item"
                  @click="triggerOpenUploadFile"
                >
                  attach_file
                </span>
              </file-upload>
            </div>
            <div v-if="showDocalButton" class="flex">
              <span
                v-tooltip.top="$t('INTEGRATION_SETTINGS.DOCAL.CTA_MODAL.TITLE')"
                :class="{ disabled: !isDocalContactValid }"
                class="material-symbols-outlined message-toolbar-item"
                @click="isDocalContactValid && toggleDocal()"
              >
                contract_edit
              </span>
              <docal-documents-modal
                :show="showDocalModal"
                :conversation-id="conversationId"
                :inbox-id="inbox.id"
                @signing-link="onDocalSigningLink"
                @cancel="showDocalModal = false"
              />
            </div>
            <AIAssistanceButton
              v-if="hasInboxPermission('Inbox ai assistance')"
              :conversation-id="conversationId"
              :is-private-note="isOnPrivateNote"
              :message="message"
              @replace-text="replaceText"
            />
            <bafi-file-attacher
              v-if="showFileUpload && isEmailOrWebWidgetInbox"
              :on-file-download="onFileUpload"
            />
          </template>
          <template #toolbar-right>
            <div
              :dir="isRTLView ? 'rtl' : 'ltr'"
              :class="`right-wrap ${isRTLView ? 'rtl' : ''}`"
            >
              <div class="flex items-center gap-2">
                <span
                  v-if="
                    !isOnPrivateNote &&
                    hasInboxPermission('Inbox delay message')
                  "
                  class="material-symbols-outlined w-10 h-10 rounded-[5px] flex items-center justify-center text-[24px]/[24px]"
                  :class="{
                    'cursor-not-allowed text-gray-400 hover:transparent':
                      isReplyButtonDisabled,
                    'hover:bg-neutral-100 text-gray-500 cursor-pointer':
                      !isReplyButtonDisabled,
                  }"
                  @click="toggleDelayModal"
                >
                  alarm
                </span>
                <div
                  v-tooltip.top="replyButtonTooltip"
                  class="button-filled flex gap-1 justify-center items-center h-[40px]"
                  :class="{
                    'flex-row-reverse ltr:pr-4 ltr:pl-2 rtl:pl-4 rtl:pr-4': isNote,
                    'ltr:pl-4 ltr:pr-2 rtl:pr-4 rtl:pl-4': !isNote,
                  }"
                  :disabled="isReplyButtonDisabled"
                  @click="onSendReply"
                >
                  <span class="text-base">{{ replyButtonLabel }}</span>
                  <span
                    class="material-symbols-outlined hover:!bg-transparent text-[24px]/[24px]"
                    style="color: inherit"
                  >
                    {{ isNote ? 'draft' : 'send' }}
                  </span>
                </div>
              </div>
            </div>
          </template>
        </email-woot-message-editor>
        <woot-modal
          :show.sync="showDelayModal"
          :on-close="hideDelayModal"
          class="delay-message-modal"
        >
          <delay-message-modal
            :hours="Array.from({ length: 24 }, (_, i) => i + 1)"
            @set-delay="setDelay"
            @close="hideDelayModal"
          />
        </woot-modal>
      </div>
    </div>
    <div
      v-if="false && hasAttachments"
      class="attachment-preview-box"
      @paste="onPaste"
    >
      <attachment-preview
        class="flex-col mt-4"
        :attachments="attachedFiles"
        :remove-attachment="removeAttachment"
      />
    </div>
    <message-signature-missing-alert
      v-if="isSignatureEnabledForInbox && !isSignatureAvailable"
    />

    <woot-confirm-modal
      ref="confirmDialog"
      :title="$t('CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.TITLE')"
      :description="undefinedVariableMessage"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import alertMixin from 'shared/mixins/alertMixin';
import FileUpload from 'vue-upload-component';

import AttachmentPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import ReplyEmailHead from './ReplyEmailHead.vue';
import MessageSignatureMissingAlert from 'dashboard/components/widgets/conversation/MessageSignatureMissingAlert';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants.js';
import EmailWootMessageEditor from 'dashboard/components/widgets/WootWriter/EmailEditor/IndexV2.vue';
import { ALLOWED_FILE_TYPES } from 'shared/constants/messages';
import inboxMixin, { INBOX_TYPES } from 'shared/mixins/inboxMixin';
import { isFileExtAllowed } from 'dashboard/helper/commons.js';

const DocalDocumentsModal = () =>
  import('dashboard/components/widgets/conversation/DocalDocumentsModal.vue');
const BafiFileAttacher = () =>
  import(
    /* webpackChunkName: "platform_views" */ 'dashboard/components/widgets/online99_platform/BafiFileAttacher.vue'
  );
import messageFormatterMixin from 'shared/mixins/messageFormatterMixin';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  getMessageVariables,
  getUndefinedVariablesInMessage,
  replaceVariablesInMessage,
} from '@chatwoot/utils';
import { buildHotKeys } from 'shared/helpers/KeyboardHelpers';
import { MESSAGE_MAX_LENGTH } from 'shared/helpers/MessageTypeHelper';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import { trimContent, debounce } from '@chatwoot/utils';
import wootConstants from 'dashboard/constants/globals';
import { isEditorHotKeyEnabled } from 'dashboard/mixins/uiSettings';
import { CONVERSATION_EVENTS } from 'dashboard/helper/AnalyticsHelper/events';
import rtlMixin from 'shared/mixins/rtlMixin';
import conversationMixin from 'dashboard/mixins/conversations';
import fileUploadMixin from 'dashboard/mixins/fileUploadMixin';
import {
  appendSignature,
  removeSignature,
  replaceSignature,
  extractTextFromMarkdown,
} from 'dashboard/helper/editorHelper';

import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';
import { LocalStorage } from 'shared/helpers/localStorage';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
import AIAssistanceButton from 'dashboard/components/widgets/AIAssistanceButtonV2.vue';
import DelayMessageModal from 'dashboard/components/widgets/modal/DelayMessageModal.vue';
import { EMAIL_EDITOR_MENU_OPTIONS } from 'dashboard/constants/editor';
import { MESSAGE_TYPE } from 'shared/constants/messages';

export default {
  components: {
    EmailWootMessageEditor,
    AttachmentPreview,
    ReplyEmailHead,
    MessageSignatureMissingAlert,
    FileUpload,
    DocalDocumentsModal,
    BafiFileAttacher,
    AIAssistanceButton,
    DelayMessageModal,
  },
  mixins: [
    clickaway,
    inboxMixin,
    uiSettingsMixin,
    alertMixin,
    messageFormatterMixin,
    rtlMixin,
    fileUploadMixin,
    conversationMixin,
    hasPermissionMixin,
  ],
  props: {
    closeReplyBox: {
      type: Function,
      default: null,
    },
    popoutReplyBox: {
      type: Boolean,
      default: false,
    },
    showTopPanel: {
      type: Boolean,
      default: true,
    },
    isShowing: {
      type: Boolean,
      default: false,
    },
    messageData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      message: '',
      inReplyTo: {},
      isFocused: false,
      showEmojiPicker: false,
      attachedFiles: [],
      isUploading: false,
      replyType: this.$store.getters['draftMessages/getReplyEditorMode'],
      bccEmails: '',
      fwdEmails: '',
      fromEmail: '',
      ccEmails: '',
      toEmails: '',
      doAutoSaveDraft: () => {},
      updateEditorSelectionWith: '',
      undefinedVariableMessage: '',
      showUserMentions: false,
      showCannedMenu: false,
      showVariablesMenu: false,
      newConversationModalActive: false,
      showDocalModal: false,
      delayHours: null,
      isShowingReplyBox: this.isShowing,
      showToolbar: false,
      showDelayModal: false,
      emailEditorMenuOptions: EMAIL_EDITOR_MENU_OPTIONS,
    };
  },
  computed: {
    ...mapGetters({
      currentChat: 'getSelectedChat',
      getMessageSignature: 'getMessageSignature',
      currentUser: 'getCurrentUser',
      lastEmail: 'getLastEmailInSelectedChat',
      lastOutgoingEmail: 'getLastOutgoingMessageInSelectedChat',
      globalConfig: 'globalConfig/get',
      accountId: 'getCurrentAccountId',
      getAccount: 'accounts/getAccount',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
    }),
    account() {
      return this.getAccount(this.accountId);
    },
    isEmailOrWebWidgetInbox() {
      return this.isAnEmailChannel || this.channelType === INBOX_TYPES.WEB;
    },
    showDocalButton() {
      return this.account?.additional_attributes?.document_signature_enabled;
    },
    isDocalContactValid() {
      const contact = this.currentChat?.meta?.sender;
      if (this.isAnEmailChannel) return !!contact?.email;
      if (this.isASmsInbox) return !!contact?.phone_number;
      return false;
    },
    allowedFileTypes() {
      return ALLOWED_FILE_TYPES;
    },
    charLengthClass() {
      return this.charactersRemaining < 0 ? 'text-red-600' : 'text-slate-600';
    },
    characterLengthWarning() {
      return this.charactersRemaining < 0
        ? `${-this.charactersRemaining} ${this.$t(
            'CONVERSATION.REPLYBOX.CHAR_LENGTH_WARNING.NEGATIVE'
          )}`
        : `${this.charactersRemaining} ${this.$t(
            'CONVERSATION.REPLYBOX.CHAR_LENGTH_WARNING.UNDER_50'
          )}`;
    },
    messageSignature() {
      const signature = this.getMessageSignature(this.inboxId)?.body || '';
      if (this.attachmentSignature) {
        return (
          signature +
          `\n ![This is signature](${this.attachmentSignature} "This is signature")`
        );
      }
      return signature;
    },
    attachmentSignature() {
      return this.getMessageSignature(this.inboxId)?.attachment_url;
    },
    showRichContentEditor() {
      return true;
    },
    assignedAgent() {
      return this.currentChat.meta.assignee;
    },
    showSelfAssignBanner() {
      if (this.message !== '' && !this.isOnPrivateNote) {
        if (!this.assignedAgent) {
          return true;
        }
        if (this.assignedAgent.id !== this.currentUser.id) {
          return true;
        }
      }

      return false;
    },
    isPrivate() {
      if (this.currentChat.can_reply || this.isAWhatsAppChannel) {
        return this.isOnPrivateNote;
      }
      return true;
    },
    inboxId() {
      return this.currentChat.inbox_id;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.inboxId);
    },
    messagePlaceHolder() {
      return this.isPrivate
        ? this.$t('CONVERSATION.FOOTER.PRIVATE_MSG_INPUT')
        : this.$t('CONVERSATION.FOOTER.MSG_INPUT');
    },
    isMessageLengthReachingThreshold() {
      return this.message.length > this.maxLength - 50;
    },
    charactersRemaining() {
      return this.maxLength - this.message.length;
    },
    isReplyButtonDisabled() {
      if (!this.hasInboxPermission('Inbox reply')) return true;
      if (this.hasAttachments || this.hasRecordedAudio) return false;

      return (
        this.isMessageEmpty ||
        this.message.length === 0 ||
        this.message.length > this.maxLength
      );
    },
    sender() {
      return {
        name: this.currentUser.name,
        thumbnail: this.currentUser.avatar_url,
      };
    },
    conversationType() {
      const { additional_attributes: additionalAttributes } = this.currentChat;
      const type = additionalAttributes ? additionalAttributes.type : '';
      return type || '';
    },
    maxLength() {
      return MESSAGE_MAX_LENGTH.GENERAL;
    },
    showFileUpload() {
      return this.hasInboxPermission('Inbox attach files');
    },
    replyButtonTooltip() {
      let sendMessageText = this.$t('CONVERSATION.REPLYBOX.SEND');
      if (this.isPrivate) {
        sendMessageText = this.$t('CONVERSATION.REPLYBOX.CREATE');
      }
      const keyLabel = isEditorHotKeyEnabled(this.uiSettings, 'cmd_enter')
        ? '(ctrl + ↵)'
        : '(↵)';
      return `${sendMessageText} ${keyLabel}`;
    },
    replyButtonLabel() {
      if (this.isPrivate) return this.$t('CONVERSATION.REPLYBOX.CREATE');
      return this.$t('CONVERSATION.REPLYBOX.SEND');
    },
    hasAttachments() {
      return this.attachedFiles.length;
    },
    hasRecordedAudio() {
      return (
        this.$refs.audioRecorderInput &&
        this.$refs.audioRecorderInput.hasAudio()
      );
    },
    isOnPrivateNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    isReplyAll() {
      return this.replyType === REPLY_EDITOR_MODES.REPLY_ALL;
    },
    isMessageEmpty() {
      if (!this.message) {
        return true;
      }
      return !this.message.trim().replace(/\n/g, '').length;
    },
    isSignatureEnabledForInbox() {
      return !this.isPrivate && this.sendWithSignature;
    },
    isSignatureAvailable() {
      return !!this.signatureToApply;
    },
    sendWithSignature() {
      return !!this.messageSignature;
    },
    editorMessageKey() {
      const { editor_message_key: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    commandPlusEnterToSendEnabled() {
      return this.editorMessageKey === 'cmd_enter';
    },
    enterToSendEnabled() {
      return this.editorMessageKey === 'enter';
    },
    conversationId() {
      return this.currentChat.id;
    },
    conversationIdByRoute() {
      return this.conversationId;
    },
    editorStateId() {
      return `draft-${this.conversationIdByRoute}-${this.replyType}`;
    },
    messageVariables() {
      const variables = getMessageVariables({
        conversation: this.currentChat,
      });
      return variables;
    },
    signatureToApply() {
      return this.messageSignature;
    },
    isNote() {
      return this.replyType === REPLY_EDITOR_MODES.NOTE;
    },
    fileUploadOptions() {
      return {
        isMailboxFileUpload: this.isAnEmailChannel,
        isWhatsAppFileUpload: this.isAWhatsAppChannel,
      };
    },
    emailFromAddress() {
      return this.inbox?.email || '';
    },
    isIncomingMessage() {
      return this.messageData.message_type === MESSAGE_TYPE.INCOMING;
    },
  },
  watch: {
    showSelfAssignBanner(val) {
      this.$emit('update:showSelfAssignBanner', val);
    },
    currentChat(conversation, oldConversation) {
      if (conversation.id === oldConversation.id) return;

      const { can_reply: canReply } = conversation;

      if (this.isOnPrivateNote) {
        return;
      }

      if (canReply || this.isAWhatsAppChannel) {
        this.replyType = REPLY_EDITOR_MODES.REPLY;
      } else {
        this.replyType = REPLY_EDITOR_MODES.NOTE;
      }

      this.setCCAndToEmailsFromLastChat();
      this.fetchAndSetReplyTo();
    },
    conversationIdByRoute(conversationId, oldConversationId) {
      if (conversationId !== oldConversationId) {
        this.setToDraft(oldConversationId, this.replyType);
        this.getFromDraft();
      }
    },
    message() {
      this.doAutoSaveDraft();
    },
    replyType(updatedReplyType, oldReplyType) {
      this.setToDraft(this.conversationIdByRoute, oldReplyType);
      this.getFromDraft();
    },
    isShowing(newVal) {
      if (newVal) {
        this.setCCAndToEmailsFromLastChat();
      }
    },
    isShowingReplyBox() {
      this.$emit('update:isShowing', this.isShowingReplyBox);
    },
  },

  mounted() {
    this.doAutoSaveDraft = debounce(
      () => {
        this.saveDraft(this.conversationIdByRoute, this.replyType);
      },
      500,
      true
    );

    this.getFromDraft();
    this.$el.addEventListener('paste', this.onPaste);
    document.addEventListener('keydown', this.handleKeyEvents);
    this.setCCAndToEmailsFromLastChat();

    this.fetchAndSetReplyTo();
    bus.$on(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    bus.$on(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.onToggleNote);
    if (this.closeReplyBox) {
      bus.$on(BUS_EVENTS.CLOSE_REPLY, this.closeReplyBox);
    }
    bus.$on(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    this.observeReplyBoxResize();
  },
  beforeDestroy() {
    this.$el.removeEventListener('paste', this.onPaste);
    document.removeEventListener('keydown', this.handleKeyEvents);
    bus.$off(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE, this.fetchAndSetReplyTo);
    bus.$off(BUS_EVENTS.TOGGLE_FORWARD_TO_MESSAGE, this.onToggleNote);
    if (this.closeReplyBox) {
      bus.$off(BUS_EVENTS.CLOSE_REPLY, this.closeReplyBox);
    }
    bus.$off(
      BUS_EVENTS.NEW_CONVERSATION_MODAL,
      this.onNewConversationModalActive
    );
    this.unobserveReplyBoxResize();
  },
  methods: {
    triggerOpenUploadFile() {
      this.$refs.uploadAttachment.$el
        .querySelector('input[type="file"]')
        .click();
    },
    toggleDocal() {
      this.showDocalModal = !this.showDocalModal;
    },
    onDocalSigningLink(url) {
      this.showDocalModal = false;
      this.$refs.messageEditor.insertContentIntoEditor(url);
    },
    toggleRichContentEditor() {
      this.updateUISettings({
        display_rich_content_editor: !this.showRichContentEditor,
      });

      const plainTextSignature = extractTextFromMarkdown(this.messageSignature);

      if (!this.showRichContentEditor && this.messageSignature) {
        let message = removeSignature(this.message, this.messageSignature);
        message = extractTextFromMarkdown(message);
        message = appendSignature(message, plainTextSignature);

        this.message = message;
      } else {
        this.message = replaceSignature(
          this.message,
          plainTextSignature,
          this.messageSignature
        );
      }
    },
    saveDraft(conversationId, replyType) {
      const key = `draft-${conversationId}-${replyType}`;
      const draftToSave = trimContent(this.message || '');

      this.$store.dispatch('draftMessages/set', {
        key,
        message: draftToSave,
      });
    },
    setToDraft(conversationId, replyType) {
      this.saveDraft(conversationId, replyType);
      this.message = '';
    },
    getFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        const messageFromStore =
          this.$store.getters['draftMessages/get'](key) || '';

        this.message = this.toggleSignatureForDraft(messageFromStore);
      }
    },
    toggleSignatureForDraft(message) {
      if (this.isPrivate) {
        return message;
      }

      return this.sendWithSignature
        ? appendSignature(message, this.signatureToApply)
        : removeSignature(message, this.signatureToApply);
    },
    removeFromDraft() {
      if (this.conversationIdByRoute) {
        const key = `draft-${this.conversationIdByRoute}-${this.replyType}`;
        this.$store.dispatch('draftMessages/delete', { key });
      }
    },
    handleKeyEvents(e) {
      const keyCode = buildHotKeys(e);
      if (keyCode === 'escape') {
        this.hideEmojiPicker();
      } else if (keyCode === 'meta+k') {
        const ninja = document.querySelector('ninja-keys');
        ninja.open();
        e.preventDefault();
      } else if (keyCode === 'enter' && this.isAValidEvent('enter')) {
        this.onSendReply();
        e.preventDefault();
      } else if (
        ['meta+enter', 'ctrl+enter'].includes(keyCode) &&
        this.isAValidEvent('cmd_enter')
      ) {
        this.onSendReply();
      }
    },
    isAValidEvent(selectedKey) {
      return (
        !this.showUserMentions &&
        !this.showCannedMenu &&
        !this.showVariablesMenu &&
        this.isFocused &&
        isEditorHotKeyEnabled(this.uiSettings, selectedKey)
      );
    },
    onPaste(e) {
      if (!this.hasInboxPermission('Inbox attach files')) {
        return;
      }
      const data = e.clipboardData.files;
      if (!data.length || !data[0]) {
        return;
      }
      data.forEach(file => {
        const { name, type, size } = file;
        this.fileUploadHandler({ name, type, size, file: file }, true);
      });
    },
    toggleUserMention(currentMentionState) {
      this.showUserMentions = currentMentionState;
    },
    toggleCannedMenu(value) {
      this.showCannedMenu = value;
    },
    toggleVariablesMenu(value) {
      this.showVariablesMenu = value;
    },
    confirmOnSendReply() {
      if (this.isReplyButtonDisabled) {
        return;
      }
      const messagePayload = this.getMessagePayload(this.message);
      this.sendMessage(messagePayload);

      if (!this.isPrivate) {
        this.clearEmailField();
      }

      this.clearMessage();
      this.hideEmojiPicker();
      this.$emit('update:isShowing', false);
      this.$emit('update:popoutReplyBox', false);
    },
    sendMessageAnalyticsData(isPrivate) {
      return isPrivate
        ? this.$track(CONVERSATION_EVENTS.SENT_PRIVATE_NOTE)
        : this.$track(CONVERSATION_EVENTS.SENT_MESSAGE, {
            channelType: this.channelType,
            signatureEnabled: this.sendWithSignature,
            hasReplyTo: !!this.inReplyTo?.id,
          });
    },
    async setDelay(delayHours) {
      this.delayHours = delayHours;
      await this.onSendReply();
      this.delayHours = null;
    },
    async onSendReply() {
      const undefinedVariables = getUndefinedVariablesInMessage({
        message: this.message,
        variables: this.messageVariables,
      });
      if (undefinedVariables.length > 0) {
        const undefinedVariablesCount =
          undefinedVariables.length > 1 ? undefinedVariables.length : 1;
        this.undefinedVariableMessage = this.$t(
          'CONVERSATION.REPLYBOX.UNDEFINED_VARIABLES.MESSAGE',
          {
            undefinedVariablesCount,
            undefinedVariables: undefinedVariables.join(', '),
          }
        );

        const ok = await this.$refs.confirmDialog.showConfirmation();
        if (ok) {
          this.confirmOnSendReply();
        }
      } else {
        this.confirmOnSendReply();
      }
    },
    async sendMessage(messagePayload) {
      try {
        await this.$store.dispatch(
          'createPendingMessageAndSend',
          messagePayload
        );
        bus.$emit(BUS_EVENTS.SCROLL_TO_MESSAGE);
        bus.$emit(BUS_EVENTS.MESSAGE_SENT);
        this.removeFromDraft();
        this.sendMessageAnalyticsData(messagePayload.private);
      } catch (error) {
        const errorMessage =
          error?.response?.data?.error || this.$t('CONVERSATION.MESSAGE_ERROR');
        this.showAlert(errorMessage);
      }
    },
    replaceText(message) {
      const updatedMessage = replaceVariablesInMessage({
        message,
        variables: this.messageVariables,
      });
      setTimeout(() => {
        this.$track(CONVERSATION_EVENTS.INSERTED_A_CANNED_RESPONSE);
        this.message = updatedMessage;
      }, 100);
    },
    insertText(text) {
      const currentMsg = this.message || '';
      const separator = currentMsg.trim() ? ' ' : '';
      this.message = currentMsg + separator + text;
    },
    setReplyMode(mode = REPLY_EDITOR_MODES.REPLY) {
      if (mode === REPLY_EDITOR_MODES.FORWARD) {
        this.$emit('forward');
        return;
      }
      const { can_reply: canReply } = this.currentChat;
      this.$store.dispatch('draftMessages/setReplyEditorMode', {
        mode,
      });
      if (canReply || this.isAWhatsAppChannel) this.replyType = mode;
    },
    clearEditorSelection() {
      this.updateEditorSelectionWith = '';
    },
    emojiOnClick(emoji) {
      this.updateEditorSelectionWith = emoji;
      this.onFocus();
    },
    clearMessage() {
      this.message = '';
      if (this.sendWithSignature && !this.isPrivate) {
        this.message = appendSignature(this.message, this.signatureToApply);
      }
      this.attachedFiles = [];
      this.resetReplyToMessage();
    },
    clearEmailField() {
      this.ccEmails = '';
      this.bccEmails = '';
      this.fwdEmails = '';
      this.fromEmail = '';
      this.toEmails = '';
    },
    toggleEmojiPicker() {
      this.showEmojiPicker = !this.showEmojiPicker;
    },
    hideEmojiPicker() {
      if (this.showEmojiPicker) {
        this.toggleEmojiPicker();
      }
    },
    onTypingOn() {
      this.toggleTyping('on');
    },
    onTypingOff() {
      this.toggleTyping('off');
    },
    onBlur() {
      this.isFocused = false;
      this.saveDraft(this.conversationIdByRoute, this.replyType);
    },
    onFocus() {
      this.isFocused = true;
    },
    toggleTyping(status) {
      const conversationId = this.currentChat.id;
      const isPrivate = this.isPrivate;

      if (!conversationId) {
        return;
      }

      this.$store.dispatch('conversationTypingStatus/toggleTyping', {
        status,
        conversationId,
        isPrivate,
      });
    },
    attachFile({ blob, file }) {
      const reader = new FileReader();
      reader.readAsDataURL(file.file);
      reader.onloadend = () => {
        this.attachedFiles.push({
          currentChatId: this.currentChat.id,
          resource: blob || file,
          isPrivate: this.isPrivate,
          thumb: reader.result,
          blobSignedId: blob ? blob.signed_id : undefined,
        });
      };
    },
    removeAttachment(itemIndex) {
      this.attachedFiles = this.attachedFiles.filter(
        (item, index) => itemIndex !== index
      );
    },
    getMessagePayload(message) {
      const messagePayload = {
        conversationId: this.currentChat.id,
        message,
        private: this.isPrivate,
        sender: this.sender,
        delay_hours: this.delayHours,
      };

      if (this.inReplyTo?.id) {
        messagePayload.contentAttributes = {
          in_reply_to: this.inReplyTo.id,
        };
      }
      if (this.isAPIInbox && this.messageSignature) {
        messagePayload.contentAttributes = {
          ...messagePayload.contentAttributes,
          message_signature: this.messageSignature,
        };
      }
      if (this.attachedFiles && this.attachedFiles.length) {
        messagePayload.files = messagePayload.files || [];
        this.attachedFiles.forEach(attachment => {
          if (this.globalConfig.directUploadsEnabled) {
            messagePayload.files.push(attachment.blobSignedId);
          } else {
            messagePayload.files.push(attachment.resource.file);
          }
        });
      }

      if (!this.isOnPrivateNote) {
        if (this.fwdEmails) {
          messagePayload.toEmails = this.fwdEmails;
          messagePayload.forwarded = true;
        } else if (this.toEmails) {
          messagePayload.toEmails = this.toEmails;
        }
        if (this.ccEmails) messagePayload.ccEmails = this.ccEmails;
        if (this.bccEmails) messagePayload.bccEmails = this.bccEmails;
        if (this.fromEmail) messagePayload.fromEmail = this.fromEmail;
      }

      return messagePayload;
    },
    setCCAndToEmailsFromLastChat() {
      if (!this.isAnEmailChannel) return;
      // if (this.isAnEmailApiChannel && this.lastOutgoingEmail) {
      //   this.fromEmail = this.lastOutgoingEmail.content_attributes?.from_email;
      // }
      let conversationContact = this.currentChat?.meta?.sender?.email || '';
      if (this.isIncomingMessage) {
        conversationContact =
          this.messageData.content_attributes?.email?.from?.[0] ??
          conversationContact;
      }

      if (!this.lastEmail) {
        this.toEmails = conversationContact;
        if (this.isReplyAll) {
          this.ccEmails =
            this.messageData.content_attributes?.cc_emails?.join(', ');
          this.bccEmails =
            this.messageData.content_attributes?.bcc_emails?.join(', ');
        }
        return;
      }

      const { content_attributes } = this.isIncomingMessage
        ? this.messageData
        : this.lastEmail;
      const { email: emailAttributes = {} } = content_attributes;

      let cc = [];
      let to = [];
      if (this.isReplyAll) {
        cc = emailAttributes.cc ? [...emailAttributes.cc, ...cc] : cc;
        if (!emailAttributes.to.includes(this.inbox.email)) {
          cc = emailAttributes.to ? [...emailAttributes.to, ...cc] : cc;
        }
      }
      cc = cc.filter(i => i !== this.inbox?.email);

      if (cc.includes(conversationContact)) {
        cc = cc.filter(email => email !== conversationContact);
      }

      const fromList = emailAttributes.from || [];
      if (!fromList.includes(conversationContact)) {
        to.push(...fromList);
        if (this.isReplyAll) {
          cc.push(conversationContact);
        }
      } else {
        to.push(conversationContact);
      }

      if (to.length === 0) {
        to.push(conversationContact);
      }

      let bcc = (emailAttributes.bcc || []).filter(
        email => email !== conversationContact
      );

      bcc = [...new Set(bcc)];
      cc = [...new Set(cc)];
      to = [...new Set(to)];

      this.ccEmails = cc.join(', ');
      this.bccEmails = bcc.join(', ');
      this.toEmails = to.join(', ');
      this.fwdEmails = '';
    },
    fetchAndSetReplyTo() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      const replyToMessageId = LocalStorage.getFromJsonStore(
        replyStorageKey,
        this.conversationId
      );
      const mode = LocalStorage.getFromJsonStore(replyStorageKey, 'mode');
      if (mode && replyToMessageId) {
        this.setReplyMode(mode);
      }
      this.inReplyTo = this.currentChat?.messages?.find(
        message => message.id === replyToMessageId
      );
    },
    onToggleNote() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_FORWARD_TO;

      const mode = LocalStorage.getFromJsonStore(replyStorageKey, 'mode');
      if (mode) {
        this.setReplyMode(mode);
      }
    },
    resetReplyToMessage() {
      const replyStorageKey = LOCAL_STORAGE_KEYS.MESSAGE_REPLY_TO;
      LocalStorage.deleteFromJsonStore(replyStorageKey, this.conversationId);
      bus.$emit(BUS_EVENTS.TOGGLE_REPLY_TO_MESSAGE);
    },
    onNewConversationModalActive(isActive) {
      this.newConversationModalActive = isActive;
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        if (!isFileExtAllowed(newFile.name, this.fileKindsAcceptedToUpload)) {
          return prevent();
        }
      }
    },
    toggleDelayModal() {
      if (this.isReplyButtonDisabled) return;
      this.showDelayModal = !this.showDelayModal;
    },
    hideDelayModal() {
      this.showDelayModal = false;
    },
    observeReplyBoxResize() {
      let replyBoxHeight = null;
      this.replyBoxResizeObserver = new ResizeObserver(([entry]) => {
        const newHeight = entry.contentRect.height;
        if (replyBoxHeight !== null) {
          const delta = newHeight - replyBoxHeight;
          const conversationPanel = this.$el.closest('.conversation-panel');
          if (delta && conversationPanel) {
            conversationPanel.scrollTop += delta;
          }
        }
        replyBoxHeight = newHeight;
      });
      this.replyBoxResizeObserver.observe(this.$el);
    },
    unobserveReplyBoxResize() {
      this.replyBoxResizeObserver?.disconnect();
      this.replyBoxResizeObserver = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.send-button {
  @apply mb-0;
}

.attachment-preview-box {
  @apply bg-transparent py-0 px-4;
}

.reply-box {
  @apply border rounded-lg overflow-hidden dark:border-slate-700 dark:bg-slate-900;

  &.is-private {
    @apply bg-yellow-50 dark:bg-yellow-200;
  }
}

.reply-box__top {
  @apply relative py-0 -mt-px border-t border-solid border-slate-50 dark:border-slate-700;
}

.normal-editor__canned-box {
  width: calc(100% - 2 * var(--space-normal));
  left: var(--space-normal);
}

.delay-message-modal {
  ::v-deep {
    .icon.icon--font.dismiss {
      margin: 0;
    }
  }
}
</style>
